/* Reformly Home — hero carousel transport.

   Mirrors the export's own behaviour rather than a library:

     - a flex row of full-width slides moved with translate3d
     - 1100ms on cubic-bezier(0.62,0.02,0.2,1) (declared in the CSS)
     - advance every 7000ms (the `data-ms` attribute, a section setting)
     - the slide list carries a trailing CLONE of slide 0, so the loop runs
       forward into it and then jumps back to real index 0 with the transition
       suppressed — no backwards rewind through every slide

   No Swiper/Embla: this is one linear translate with no free drag, no
   per-slide widths and no paging maths. CLAUDE.md's carousel preference is
   about rails that need those; shipping a library for a 100%-width translate
   would be bytes for nothing. There is also no scroll container here, so the
   scroll-chaining class of bug the porting doc warns about cannot apply.
*/
(function () {
  var roots = document.querySelectorAll('[data-rhm-hero]');
  if (!roots.length) return;

  Array.prototype.forEach.call(roots, function (root) {
    var track = root.querySelector('[data-rhm-hero-track]');
    var dots = root.querySelectorAll('[data-rhm-hero-dot]');
    var count = parseInt(root.getAttribute('data-count'), 10) || 0;
    var ms = parseInt(root.getAttribute('data-ms'), 10) || 7000;
    if (!track || count < 2) return;

    var i = 0;        /* 0 .. count, where `count` is the clone */
    var timer = null;
    var animating = false;

    function paint() {
      track.style.setProperty('--rhm-hero-i', i);

      /* The clone shows slide 0's content, so the dots must read as slide 0
         while it is on screen. */
      var active = i % count;

      Array.prototype.forEach.call(dots, function (dot, n) {
        dot.setAttribute('aria-current', n === active ? 'true' : 'false');
        dot.classList.toggle('is-past', n < active);

        /* Restart the fill animation on the newly active dot. Reassigning the
           same animation name does nothing on its own — the element has to
           leave and re-enter the animating state, which is what the reflow
           read forces. Cheap: one dot, once per 7s. */
        var fill = dot.firstElementChild;
        if (fill && n === active) {
          fill.style.animation = 'none';
          void fill.offsetWidth;
          fill.style.animation = '';
        }
      });
    }

    var settleTimer = null;

    function go(next) {
      if (animating) return;
      animating = true;
      i = next;
      track.classList.remove('is-snapping');
      track.classList.add('is-moving');
      paint();

      /* Belt to transitionend's braces.

         `animating` is cleared by transitionend — but an INTERRUPTED
         transition never fires one, and a swipe interrupts the transport by
         design (the finger takes over mid-flight, and `is-dragging` sets
         transition:none). The flag then latched true for good and the
         carousel froze: every later swipe and dot tap hit the guard in here
         and returned. This timer is the floor under that. */
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 1250);   /* the 1100ms transport + slack */
    }

    /* Runs at the end of the transport, from whichever of the two gets there
       first. When we have just landed on the clone, swap to the real slide 0
       with transitions off so the jump is invisible, then restore them on the
       next frame — setting the class and the transform in the same frame
       would otherwise still animate. */
    function settle() {
      if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
      animating = false;
      track.classList.remove('is-moving');
      if (i === count) {
        track.classList.add('is-snapping');
        i = 0;
        track.style.setProperty('--rhm-hero-i', 0);
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            track.classList.remove('is-snapping');
          });
        });
      }
    }

    track.addEventListener('transitionend', function (e) {
      if (e.propertyName !== 'transform') return;
      settle();
    });

    function start() {
      stop();

      /* THE INDICATOR HAS TO RESTART WITH THE CLOCK.

         start() always resets the interval, and it is now called every time a
         scroll settles as well as on re-entry from the observer. The dot's
         fill is a 7s CSS animation that only ever restarts inside paint(), on
         a slide CHANGE — so after a scroll the fill carried on from wherever
         it was while the interval began again from zero. The bar reached the
         end with no advance, which reads as the carousel having stalled.

         Repainting here puts the two back on the same clock: the fill restarts
         when the countdown does. Cheap — one dot, and only when autoplay
         resumes. */
      paint();

      /* Safety on the `animating` latch. go() clears it from transitionend or
         the 1250ms backstop, but neither fires if the transport was started
         while the hero was off-screen and the tab never composited it. A
         latched flag makes every later tick return early and the carousel
         never advances again. No settleTimer pending means nothing is in
         flight, so the flag is stale by definition. */
      if (animating && !settleTimer) {
        animating = false;
        track.classList.remove('is-moving');
      }

      timer = setInterval(function () { go(i + 1); }, ms);
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    /* On the trailing clone the dots read as slide 0 but the track is parked
       one slide PAST the last one. Translating straight to `n` from there runs
       the whole way backwards through every slide. Re-park on the real slide 0
       first, with the transition suppressed, so the move transports forward
       from 0 like it does from anywhere else. Shared by the dots and the
       swipe, because both can be triggered while the clone is showing. */
    function unpark() {
      if (i !== count) return;
      track.classList.add('is-snapping');
      i = 0;
      track.style.setProperty('--rhm-hero-i', 0);
      void track.offsetWidth;
      track.classList.remove('is-snapping');
    }

    Array.prototype.forEach.call(dots, function (dot) {
      dot.addEventListener('click', function () {
        var n = parseInt(dot.getAttribute('data-rhm-hero-dot'), 10) || 0;
        if (n === i % count) return;
        animating = false;   /* a tap wins over an in-flight transport */
        unpark();
        go(n);
        start();             /* the export restarts the clock on tap */
      });
    });

    /* ---------- Swipe ----------

       Touch only. `pointerType !== 'mouse'` rather than a media query: the
       test is what the finger is, not how wide the window is, so a touch
       laptop gets it and a mouse on a narrow window does not — and a mouse
       drag keeps selecting text and following links as people expect.

       The horizontal/vertical decision is left to the browser via
       `touch-action: pan-y` (see the CSS). Once a horizontal pan begins the
       browser sends us the moves and never scrolls the page, so there is no
       axis-locking to do here and no preventDefault to get wrong.

       Threshold is 18% of the viewport or a flick faster than 0.45 px/ms —
       the second is what makes a short fast swipe work, which is most of them
       on a phone. */
    var THRESH = 0.18;
    var FLICK = 0.45;
    var dragging = false, startX = 0, startT = 0, dx = 0, pid = null;
    var raf = null, pendingX = 0;

    /* The track is moved by writing `transform` DIRECTLY during a drag, not by
       updating the --rhm-hero-dx custom property the CSS reads.

       A custom property that participates in a `calc()` inside `transform`
       invalidates style for the element and its subtree on every write, so
       sixty finger-moves a second meant sixty style recalcs over four
       full-bleed slides. Writing the finished transform is a compositor-only
       change. The property stays in the CSS for the resting state; it is
       simply not what a drag touches. */
    function dragTo(px) {
      track.style.transform =
        'translate3d(calc(' + (i * -100) + '% + ' + px + 'px), 0, 0)';
    }

    /* Hand the inline transform back so the class-driven one takes over. */
    function releaseTransform() {
      track.style.transform = '';
    }

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse') return;
      /* A finger WINS over an in-flight transport, the same way a dot tap
         does. Bailing out on `animating` instead meant that whenever autoplay
         happened to fire as the user reached for the screen, the swipe was
         silently dropped — intermittent and maddening, because it depended
         entirely on where you were in the 7s cycle. */
      if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
      animating = false;
      dragging = true;
      pid = e.pointerId;
      startX = e.clientX;
      startT = e.timeStamp;
      dx = 0;
      stop();                       /* no autoplay mid-gesture */
      unpark();                     /* never drag off the clone */
      track.classList.add('is-dragging');
    });

    /* rAF-coalesced: a phone can deliver pointermove faster than it paints,
       and writing the transform more than once per frame is work thrown away.
       Only the newest position matters, so the handler just records it. */
    track.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pid) return;
      pendingX = e.clientX;
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        if (!dragging) return;
        dx = pendingX - startX;
        /* Half resistance going BACK off slide 0 — there is nothing before it,
           and a track that follows the finger into empty space reads as broken.
           Forward has no such edge: the trailing clone is a real slide to move
           into, which is how the loop works for autoplay too. */
        if (i === 0 && dx > 0) dx *= 0.5;
        dragTo(dx);
      });
    });

    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pid)) return;
      dragging = false;
      pid = null;
      if (raf) { cancelAnimationFrame(raf); raf = null; }

      var w = root.getBoundingClientRect().width || 1;
      var dt = Math.max(1, (e ? e.timeStamp : 0) - startT);
      var fast = Math.abs(dx) / dt > FLICK;
      var far = Math.abs(dx) > w * THRESH;
      var next = i;

      /* Forward may land on `count`, the trailing clone — the same place
         autoplay goes, so transitionend performs the usual invisible re-park
         to slide 0 and the loop continues. Clamping to count-1 instead would
         dead-end the last slide, where a forward swipe is the natural way to
         come back round to the first. */
      if ((far || fast) && dx < 0) { next = Math.min(i + 1, count); }
      else if ((far || fast) && dx > 0) { next = Math.max(i - 1, 0); }

      /* A RELEASED FINGER DOES NOT GET THE AUTOPLAY TRANSPORT.

         The transport is 1100ms on cubic-bezier(0.62, 0.02, 0.2, 1) — a curve
         that is almost flat for its first fifth. Unattended that is a
         deliberate slow-in. Straight off a finger it is a dead pause before
         anything moves, then a long slide: the whole thing reads as heavy and
         broken-feeling, which is exactly how it felt.

         Both outcomes of a release therefore use the short ease-out instead.
         A swipe should finish about as fast as the hand that threw it. */
      track.classList.add('is-settling');
      track.classList.remove('is-dragging');
      releaseTransform();

      if (next !== i) go(next);

      window.setTimeout(function () {
        track.classList.remove('is-settling');
      }, 400);

      start();
      dx = 0;
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    /* Don't animate a carousel nobody is looking at: it costs a composite
       every 7s and, on a long page, runs for the whole visit. Also pauses the
       dot fill, which would otherwise be lying about when the slide changes. */
    var inView = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          inView = en.isIntersecting;
          if (inView) { start(); } else { stop(); }
        });
      }, { threshold: 0.2 }).observe(root);
    } else {
      start();
    }

    /* HOLD THE CAROUSEL WHILE THE PAGE IS ACTUALLY SCROLLING.

       The observer above only stops it once less than 20% of the hero is
       visible, which is far below the fold. Between scrollY 88 and ~260 the
       hero is still mostly on screen AND the nav is running its flush ->
       floating transition — so a 1100ms slide transport can land right on top
       of it.

       That overlap is expensive in a way no profile here shows. The nav's blur
       layer (`.rfn__card::before`, backdrop-filter) sits directly over the
       hero, and a backdrop-filter must re-sample and re-blur its backdrop on
       every frame the backdrop changes. A moving carousel underneath means
       that surface is rebuilt continuously, on top of the bar's own animation.
       Reported from an iPhone as the nav being noticeably less smooth on the
       home page than on a PDP, where nothing behind the bar moves; headless
       Chrome shows home as the CLEANER of the two, because it composites this
       cheaply and never reveals the cost.

       Pausing only while the finger is moving, rather than raising the
       observer threshold, keeps the carousel alive for someone reading
       part-way down the hero — it just gets out of the way during the one
       moment the nav needs the compositor.

       160ms after the last scroll event is long enough to cover momentum
       between flicks without the hero feeling frozen. Passive listener, and
       the only work per event is resetting a timer. */
    var scrollHold;
    window.addEventListener('scroll', function () {
      stop();
      clearTimeout(scrollHold);
      scrollHold = setTimeout(function () {
        if (inView && !document.hidden) start();
      }, 160);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); } else { start(); }
    });

    /* Respect the OS setting: hold on slide 0 rather than transporting. The
       CSS already neutralises the durations, which would make transitionend
       fire immediately and spin the loop. */
    var rm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (rm && rm.matches) {
      stop();
      start = function () {};
    }

    paint();
  });
})();
